/**
 * SeoContentPage — `/seo/content` (D1). Конвейер страниц draft→review→published→live.
 * Гейт качества: публикация заблокирована, если текст не прошёл seo_textcheck
 * (риск Баден-Баден ≥ 8) — снять правкой или оверрайдом роли seo/admin с обоснованием.
 */

import { useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { HelpButton } from '@/components/layout/HelpButton';
import { SEO_HELP } from '@/help/seoHelp';
import { Button, LoadingBlock, ErrorBlock, Badge } from '@/shared/ui';
import { useSeoPages, useSeoMutations, type SeoPage } from '@/hooks/api/useSeo';
import { useAuth } from '@/auth/useAuth';
import { ApiError } from '@/api/client';

const STATUS_TONE: Record<string, string> = { draft: 'b-neutral', review: 'b-info', published: 'b-success', live: 'b-teal' };
const PAGE_TYPES = ['comparison', 'use-case', 'pricing', 'pillar', 'cluster'];

export function SeoContentPage() {
  usePageHeader({ title: 'SEO — Контент', subtitle: 'Брифы, статусы страниц, гейт качества', actions: <HelpButton help={SEO_HELP.content} /> });
  const { user } = useAuth();
  const canOverride = user?.role === 'seo' || user?.role === 'admin';
  const { data, isLoading, isError, error, refetch } = useSeoPages();
  const { createPage } = useSeoMutations();
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [pageType, setPageType] = useState('comparison');

  const add = () => {
    if (!title.trim()) return;
    createPage.mutate({ title: title.trim(), url: url.trim(), page_type: pageType }, { onSuccess: () => { setTitle(''); setUrl(''); } });
  };

  return (
    <div className="col gap20" style={{ maxWidth: 1100 }}>
      <section className="card">
        <h3 style={{ marginTop: 0 }}>Новая страница</h3>
        <div className="row wrap" style={{ gap: 8 }}>
          <input className="input" placeholder="Заголовок/тема" value={title} onChange={(e) => setTitle(e.target.value)} style={{ minWidth: 240, flex: 1 }} />
          <input className="input" placeholder="URL (будущий)" value={url} onChange={(e) => setUrl(e.target.value)} style={{ minWidth: 200 }} />
          <select className="input" value={pageType} onChange={(e) => setPageType(e.target.value)} style={{ maxWidth: 160 }}>
            {PAGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Button variant="primary" size="sm" onClick={add} disabled={createPage.isPending || !title.trim()}>Создать</Button>
        </div>
        {createPage.isError && <ErrorBlock text={createPage.error?.message} />}
      </section>

      {isLoading && <LoadingBlock label="Загружаю страницы…" />}
      {isError && <ErrorBlock text={error?.message} onRetry={() => void refetch()} />}

      {data && (
        data.pages.length === 0 ? (
          <div className="card"><p className="muted" style={{ margin: 0 }}>Страниц нет. Создайте первую или отправьте кластер из «Семантика/карта».</p></div>
        ) : (
          <section className="card">
            <h3 style={{ marginTop: 0 }}>Контент-хаб</h3>
            <div className="col" style={{ gap: 10 }}>
              {data.pages.map((p) => <PageCard key={p.id} p={p} canOverride={canOverride} />)}
            </div>
          </section>
        )
      )}
    </div>
  );
}

function PageCard({ p, canOverride }: { p: SeoPage; canOverride: boolean }) {
  const { textcheck, patchPage, createBrief } = useSeoMutations();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [gateMsg, setGateMsg] = useState<string | null>(null);

  const tc = (p.meta?.textcheck ?? null) as { passed?: boolean; configured?: boolean; risk?: number | null } | null;

  const publish = () => {
    setGateMsg(null);
    patchPage.mutate({ id: p.id, status: 'published' }, {
      onError: (e) => {
        const err = e as ApiError;
        if (err?.code === 'textcheck_gate') {
          if (canOverride) {
            const reason = window.prompt('Публикация заблокирована гейтом качества. Причина оверрайда:');
            if (reason) patchPage.mutate({ id: p.id, status: 'published', override: true, override_reason: reason });
          } else {
            setGateMsg('Публикация заблокирована гейтом качества. Нужна правка или оверрайд роли seo/admin.');
          }
        } else {
          setGateMsg(err?.message ?? 'Ошибка');
        }
      },
    });
  };

  const genBrief = () => {
    createBrief.mutate({ pageId: p.id, title: `Бриф: ${p.title || p.url}`, body: `# Бриф для «${p.title}»\n\n> Заготовка. Полный бриф (SERP-гэпы, структура, сущности, перелинковка, обязательный FAQ/HowTo под GEO/AEO) — сгенерируйте через AI-чат: «собери бриф для ${p.url || p.title}».` });
  };

  return (
    <div className="card" style={{ background: 'var(--surface-2, #F8FAFC)', padding: '12px 14px' }}>
      <div className="row spread center wrap" style={{ gap: 8 }}>
        <div className="col" style={{ gap: 2 }}>
          <span style={{ fontWeight: 600 }}>{p.title || '(без заголовка)'}</span>
          <span className="muted mono" style={{ fontSize: 11 }}>{p.url || '—'}{p.page_type ? ` · ${p.page_type}` : ''}</span>
        </div>
        <span className="row center" style={{ gap: 6 }}>
          <Badge tone={STATUS_TONE[p.status] ?? 'b-neutral'}>{p.status}</Badge>
          {tc && (tc.configured === false
            ? <Badge tone="b-warning">текст не проверен</Badge>
            : <Badge tone={tc.passed ? 'b-success' : 'b-danger'}>{tc.passed ? 'текст ок' : `риск ${tc.risk ?? '≥8'}`}</Badge>)}
        </span>
      </div>

      <div className="row wrap" style={{ gap: 6, marginTop: 10 }}>
        <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)}>Проверить текст</Button>
        <Button size="sm" variant="outline" onClick={genBrief} disabled={createBrief.isPending}>Бриф</Button>
        {p.status !== 'published' && p.status !== 'live' && (
          <Button size="sm" variant="primary" onClick={publish} disabled={patchPage.isPending}>Опубликовать</Button>
        )}
      </div>

      {gateMsg && <p style={{ color: 'var(--danger, #DC2626)', fontSize: 12.5, marginBottom: 0 }}>{gateMsg}</p>}

      {open && (
        <div className="col" style={{ gap: 8, marginTop: 10 }}>
          <textarea className="input" rows={4} placeholder="Вставьте текст страницы для проверки (Тургенев риск Баден-Баден)" value={text} onChange={(e) => setText(e.target.value)} style={{ width: '100%' }} />
          <div className="row" style={{ gap: 8 }}>
            <Button size="sm" variant="primary" onClick={() => textcheck.mutate({ id: p.id, text })} disabled={textcheck.isPending || !text.trim()}>
              {textcheck.isPending ? 'Проверяю…' : 'Проверить'}
            </Button>
            {textcheck.data && (
              <span className="muted" style={{ fontSize: 12.5 }}>
                {textcheck.data.configured === false ? 'Тургенев не настроен — гейт мягкий' : (textcheck.data.passed ? 'Риск ниже порога — ок' : 'Риск ≥ порога — правьте текст')}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
